// Services directory — searchable list of apps with quiet status signals.
// Replaces the legacy status-sectioned tile grid: status is a filter here,
// not a layout device.
//
// Two linked filters (URL-synced): App (?apps=) and Job ID (?services=). The
// Job ID options are scoped to the selected app(s). With no filters the grid
// shows app cards; selecting any app or job switches it to a card per matching
// (app, job) pair.

import React, { useMemo, useState } from 'react';
import { Box, Chip, Paper, Skeleton, Typography } from '@mui/material';
import { GridView as ServicesIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { useV2Filters } from '../hooks/useV2Filters';
import { AppGroup, groupByApp, groupServices, OverallStatusKey } from '../lib/grouping';
import { getColors, getElevation, rounded } from '../theme/tokens';
import { reduceMotionSx, transition } from '../theme/motion';
import { DEFAULT_CONFIG } from '../../utils/needsAttentionFilter';
import PageHeader from '../components/PageHeader';
import StatusDot from '../components/StatusDot';
import ServiceCard, { statusDotStatus } from '../components/ServiceCard';
import MultiSelectFilter from '../components/MultiSelectFilter';
import EmptyState from '../components/EmptyState';
import { timeAgo } from '../lib/format';
import { serviceFilterKey, serviceFilterLabel } from '../lib/records';

const STATUS_FILTERS: Array<{ key: OverallStatusKey | 'all'; label: string; hue?: string }> = [
  { key: 'all', label: 'All' },
  { key: 'deploying', label: 'Deploying', hue: '#0b76d1' },
  { key: 'failures', label: 'Needs attention', hue: '#d93a3a' },
  { key: 'pending', label: 'Pending', hue: '#dd5b00' },
  { key: 'healthy', label: 'Healthy', hue: '#1aae39' },
  { key: 'mixed', label: 'Mixed', hue: '#787774' },
];

const APP_HUE = '#0075de';
const JOB_HUE = '#8155b8';

const AppCard: React.FC<{ group: AppGroup }> = ({ group }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  return (
    <Paper
      component={RouterLink}
      to={`/services/${encodeURIComponent(group.app)}`}
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: `${rounded.lg}px`,
        borderColor: c.hairline,
        textDecoration: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        minWidth: 0,
        transition: transition(['border-color', 'box-shadow', 'transform']),
        '&:hover': {
          borderColor: c.inkFaint,
          transform: 'translateY(-2px)',
          boxShadow: getElevation(theme.palette.mode).soft,
        },
        ...reduceMotionSx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <StatusDot status={statusDotStatus[group.status]} />
        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: c.ink }} noWrap title={group.app}>
          {group.app}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: c.inkMuted }}>
        {group.serviceCount} service{group.serviceCount !== 1 ? 's' : ''} · {group.envCount} env
        {group.envCount !== 1 ? 's' : ''}
      </Typography>
      {group.lastTimestamp && (
        <Typography variant="caption" sx={{ color: c.inkFaint }}>
          Last deploy {timeAgo(group.lastTimestamp)}
        </Typography>
      )}
    </Paper>
  );
};

const ServicesDirectory: React.FC = () => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { staticRecords, currentRecords, loading } = useDeploymentData();
  const { apps: selectedApps, services: selectedJobs, setApps, setServices } = useV2Filters();
  const [statusFilter, setStatusFilter] = useState<OverallStatusKey | 'all'>('all');

  // Job cards only appear once a job is actually selected — an app-only
  // filter narrows the app-card grid to that app instead of exploding into
  // one card per (app, job) pair.
  const showJobCards = selectedJobs.length > 0;

  // Unique apps and the jobs (serviceName) each app deploys.
  const { uniqueApps, appToJobs } = useMemo(() => {
    const appsSet = new Set<string>();
    const map = new Map<string, Set<string>>();
    staticRecords.forEach(r => {
      if (!r.app) return;
      appsSet.add(r.app);
      if (r.serviceName) {
        const jobs = map.get(r.app) ?? new Set<string>();
        jobs.add(serviceFilterKey(r.type, r.serviceName));
        map.set(r.app, jobs);
      }
    });
    return { uniqueApps: Array.from(appsSet).sort(), appToJobs: map };
  }, [staticRecords]);

  // Job options: scoped to the selected app(s), else every job across all apps.
  const jobOptions = useMemo(() => {
    const out = new Set<string>();
    const sources = selectedApps.length > 0 ? selectedApps : Array.from(appToJobs.keys());
    sources.forEach(app => appToJobs.get(app)?.forEach(j => out.add(j)));
    return Array.from(out).sort();
  }, [selectedApps, appToJobs]);

  // Changing the app selection re-scopes the job options — drop any selected
  // jobs that no longer belong to a selected app.
  const handleAppsChange = (next: string[]) => {
    setApps(next);
    if (selectedJobs.length === 0) return;
    const allowed = new Set<string>();
    const sources = next.length > 0 ? next : Array.from(appToJobs.keys());
    sources.forEach(app => appToJobs.get(app)?.forEach(j => allowed.add(j)));
    const pruned = selectedJobs.filter(j => allowed.has(j));
    if (pruned.length !== selectedJobs.length) setServices(pruned);
  };

  // Status rollups use currentRecords so stale/superseded failed & stuck
  // deploys don't keep a service flagged deploying/failing. (Filter option
  // pools above stay on staticRecords so every known app/job remains selectable.)
  const appGroups = useMemo(() => groupByApp(currentRecords, DEFAULT_CONFIG), [currentRecords]);

  // Job cards for the active filters: one card per matching (app, serviceName).
  const jobGroups = useMemo(() => {
    if (!showJobCards) return [];
    const appSet = new Set(selectedApps);
    const jobSet = new Set(selectedJobs);
    const matching = currentRecords.filter(
      r =>
        (appSet.size === 0 || (r.app != null && appSet.has(r.app))) &&
        (jobSet.size === 0 ||
          (r.serviceName != null && jobSet.has(serviceFilterKey(r.type, r.serviceName))))
    );
    return groupByApp(matching).flatMap(g =>
      groupServices(g.records).map(svc => ({ app: g.app, group: svc }))
    );
  }, [showJobCards, selectedApps, selectedJobs, currentRecords]);

  const counts = useMemo(() => {
    const out = new Map<OverallStatusKey | 'all', number>();
    if (showJobCards) {
      out.set('all', jobGroups.length);
      jobGroups.forEach(({ group }) => out.set(group.status, (out.get(group.status) || 0) + 1));
    } else {
      const scoped =
        selectedApps.length === 0 ? appGroups : appGroups.filter(g => selectedApps.includes(g.app));
      out.set('all', scoped.length);
      scoped.forEach(g => out.set(g.status, (out.get(g.status) || 0) + 1));
    }
    return out;
  }, [showJobCards, jobGroups, appGroups, selectedApps]);

  const visibleApps = useMemo(() => {
    if (showJobCards) return [];
    return appGroups.filter(
      g =>
        (selectedApps.length === 0 || selectedApps.includes(g.app)) &&
        (statusFilter === 'all' || g.status === statusFilter)
    );
  }, [showJobCards, appGroups, selectedApps, statusFilter]);

  const visibleJobs = useMemo(() => {
    if (!showJobCards) return [];
    return jobGroups.filter(({ group }) => statusFilter === 'all' || group.status === statusFilter);
  }, [showJobCards, jobGroups, statusFilter]);

  const gridStyles = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
    gap: 1.5,
  } as const;

  return (
    <Box>
      <PageHeader
        title="Services"
        subtitle={
          showJobCards
            ? 'Matching jobs, grouped by status — clear the filters to see all apps'
            : selectedApps.length > 0
            ? 'Selected app(s), grouped by status — clear the app filter to see every app'
            : 'Every app reporting deployments, grouped by status'
        }
      />

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2.5 }}>
        {/* App filter (multi-select, URL-synced via ?apps=) */}
        <MultiSelectFilter
          options={uniqueApps}
          value={selectedApps}
          onChange={handleAppsChange}
          hue={APP_HUE}
          placeholder="Filter by app…"
        />

        {/* Job ID filter (multi-select, scoped to selected apps, URL-synced via ?services=) */}
        <MultiSelectFilter
          options={jobOptions}
          value={selectedJobs}
          onChange={setServices}
          getOptionLabel={serviceFilterLabel}
          hue={JOB_HUE}
          placeholder="Filter by job…"
        />

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {STATUS_FILTERS.map(f => {
            const count = counts.get(f.key) || 0;
            if (f.key !== 'all' && count === 0) return null;
            const active = statusFilter === f.key;
            return (
              <Chip
                key={f.key}
                label={`${f.label} ${count}`}
                size="small"
                onClick={() => setStatusFilter(f.key)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.74rem',
                  backgroundColor: active ? (f.hue ? `${f.hue}1a` : c.hoverWash) : 'transparent',
                  color: active ? f.hue || c.ink : c.inkMuted,
                  border: `1px solid ${active ? (f.hue ? `${f.hue}55` : c.inkFaint) : c.hairline}`,
                  '&:hover': { backgroundColor: f.hue ? `${f.hue}14` : c.hoverWash },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {loading && staticRecords.length === 0 ? (
        <Box sx={gridStyles}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={104} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : showJobCards ? (
        visibleJobs.length === 0 ? (
          <EmptyState
            icon={<ServicesIcon />}
            title="No matching jobs"
            body="No jobs match the current app/job filters, or the status filter excludes them. Clear the filters to see all apps."
            action={
              <Chip
                label="Clear filters"
                size="small"
                onClick={() => {
                  setApps([]);
                  setServices([]);
                }}
                sx={{ fontWeight: 600, cursor: 'pointer' }}
              />
            }
          />
        ) : (
          <Box sx={gridStyles}>
            {visibleJobs.map(({ app, group }) => (
              <ServiceCard key={`${app}/${serviceFilterKey(group.type, group.serviceName)}`} app={app} group={group} />
            ))}
          </Box>
        )
      ) : visibleApps.length === 0 ? (
        <EmptyState
          icon={<ServicesIcon />}
          title="No apps match"
          body="Adjust the status filter to see more apps."
        />
      ) : (
        <Box sx={gridStyles}>
          {visibleApps.map(g => (
            <AppCard key={g.app} group={g} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ServicesDirectory;
